<template>
  <Popover :key="popoverKey" class="group">
    <!-- pb-4.5/-mb-4.5 stretch the button down to the header's bottom edge (the menu row's pb-2 plus the
         header's py-2.5) without moving anything, so the space above the underline is clickable too. -->
    <PopoverButton
      role="button"
      :as="to ? 'a' : 'div'"
      :href="to"
      class="flex gap-1 cursor-pointer text-menu-link font-medium items-center relative pb-4.5 -mb-4.5"
      :data-nav-item="label"
      :data-nav-active="(!region && isCurrentSection()) || undefined"
      @mouseenter="hoverFlyout(label)"
      @mouseleave="leaveFlyoutItem(label)"
      @mousedown="to ? suppressFlyout(label) : openFlyoutFor(label)"
      @click="handleClick">
      <span class="flex gap-1 items-center">
        <CountryFlag v-if="region" :code="region" />
        <span class="whitespace-nowrap text-base">{{ $t(label) }} </span>
      </span>
    </PopoverButton>
    <!-- Opens like apple.com: the sheet unrolls down from the menu, its content follows with a short slide and fade,
         and the backdrop fades in; closing reverses it, quicker. Moving straight from another nav item's panel swaps
         in place instead: the sheet keeps the previous height and grows or shrinks to this one while the content fades
         in. The parts animate themselves (see the styles below), so the durations are set here. -->
    <transition
      :name="isSwitching ? 'mega-menu-swap' : 'mega-menu'"
      :duration="isSwitching ? { enter: 260, leave: 0 } : { enter: 320, leave: 260 }">
      <!-- Positioned against the sticky #header, which still holds the top bar when it has slid out
           of view. `100%` is that box's bottom edge in both states; `--header-height` only counts the
           visible part, so it would lift the panel over the menu while the top bar is hidden. -->
      <PopoverPanel
        v-if="isOpen"
        as="div"
        static
        class="absolute inset-x-0 z-20 focus:outline-none"
        style="top: 100%"
        @mouseenter="keepFlyout">
        <!-- The sheet follows its content's height (see sheetHeight), so changes such as hovering a country animate.
             It only scrolls when the content is taller than it may be, so no scrollbar flashes while it grows. -->
        <div
          ref="sheet"
          class="mega-menu-sheet relative bg-surface text-surface-foreground shadow-[0px_4px_10px_rgba(0,0,0,0.15)]"
          :class="sheetScrolls ? 'overflow-y-auto' : 'overflow-hidden'"
          :style="{
            '--mega-menu-max-height': '65vh',
            maxHeight: 'var(--mega-menu-max-height)',
            height: sheetHeight === null ? undefined : `${sheetHeight}px`,
          }">
          <div ref="content" class="mega-menu-content">
            <slot :close="close"></slot>
          </div>
        </div>
        <!-- Dims and blurs the page below. Starts behind the sheet, so the page is covered while the sheet unrolls.
             Not hoverable, so moving onto it leaves the nav row and closes the panel. -->
        <div
          aria-hidden="true"
          class="mega-menu-backdrop pointer-events-none absolute inset-x-0 top-0 h-screen -z-10 bg-black/35 backdrop-blur-[5px]" />
      </PopoverPanel>
    </transition>
  </Popover>
</template>

<script setup>
import { computed, onBeforeUnmount, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useResizeObserver } from "@vueuse/core";
import { Popover, PopoverButton, PopoverPanel } from "@headlessui/vue";
import CountryFlag from "@/components/CountryFlag.vue";
import { useMegaMenuOpen } from "./mega/useMegaMenuOpen";
import { ROUTES } from "@/constants/routes";

import { useI18n } from "vue-i18n";
const { t } = useI18n();
const router = useRouter();

const props = defineProps(["label", "region", "to"]);

// Which flyout is open is shared by all nav items (see useMegaMenuOpen), so only one panel and backdrop show at a time.
// Hover timing (the delays before opening, switching and closing) lives there too. Closing on leave is handled by the
// nav row in MainMenu, so moving between items or through the gaps between them keeps the panel open.
const {
  openFlyout,
  isSwitching,
  lastSheetHeight,
  openFlyoutFor,
  closeFlyout,
  resetFlyout,
  hoverFlyout,
  suppressFlyout,
  leaveFlyoutItem,
  keepFlyout,
} = useMegaMenuOpen();
const isOpen = computed(() => openFlyout.value === props.label);

// The sheet's height in px, following its content so a change animates (`null`: its natural height, before the first
// measurement). Growing follows right away; shrinking waits a moment, because swapping the groups beside the countries
// briefly empties that space and the sheet shouldn't dip in between. The first measurement after opening (or swapping in
// from another item, where the sheet starts at that one's height) is the panel's own content, so it never waits.
const SHRINK_DELAY = 200;
const sheet = ref();
const content = ref();
const sheetHeight = ref(null);
const sheetScrolls = ref(false);
let shrinkTimer;
let isMeasured = false;

watch(isOpen, (open) => {
  clearTimeout(shrinkTimer);
  isMeasured = false;
  sheetHeight.value = open && isSwitching.value ? lastSheetHeight.value : null;
});

useResizeObserver(content, () => {
  if (!content.value || !sheet.value) return;
  const height = content.value.offsetHeight;
  const maxHeight = parseFloat(getComputedStyle(sheet.value).maxHeight);
  sheetScrolls.value = height > maxHeight;
  const applyHeight = () => {
    sheetHeight.value = height;
    lastSheetHeight.value = Math.min(height, maxHeight);
  };
  clearTimeout(shrinkTimer);
  if (!isMeasured || sheetHeight.value === null || height >= sheetHeight.value) applyHeight();
  else shrinkTimer = setTimeout(applyHeight, SHRINK_DELAY);
  isMeasured = true;
});

onBeforeUnmount(() => clearTimeout(shrinkTimer));

const popoverKey = ref(0);
const route = useRoute();

const close = () => closeFlyout(props.label);

watch(route, () => {
  popoverKey.value++;
  resetFlyout();
});

onBeforeUnmount(() => closeFlyout(props.label));

// The events page of (some of) the sport types the item links to, e.g. "Other events" on the cricket events.
const isOwnSportTypes = () => {
  const ownSportTypes = new URLSearchParams(props.to?.split("?")[1]).get("sport_type")?.split(",");
  const sportTypes = route.query?.sport_type?.toString().split(",");
  return !!ownSportTypes && !!sportTypes && sportTypes.every((sportType) => ownSportTypes.includes(sportType));
};

// The underline itself is drawn by MainMenu, which slides one bar to the hovered, open or current item.
const isCurrentSection = () => {
  if (
    (route.query?.sport_type && t(`${route.query.sport_type}`) === `${t(props.label).toLowerCase()}`) ||
    (route.fullPath === "/events?sport_type=motorsport,motogp" && props.label === `${t("Other Motorsports")}`) ||
    (route.fullPath === "/events?sport_type=horseracing" && props.label === `${t("Other events")}`) ||
    (route.fullPath === "/football/tournaments" && props.label === `${t("Football")}`) ||
    (route.fullPath === "/tennis/tournaments" && props.label === `${t("Tennis")}`) ||
    // Formula 1 links to /motorsport/formula-1, which redirects to the formula1 events page.
    (props.to === ROUTES.FORMULA_1 && route.query?.sport_type === "formula1") ||
    isOwnSportTypes()
  ) {
    return "underline";
  }
};

const handleClick = (e) => {
  if (props.to) {
    // Navigate to the page on click
    e.preventDefault();
    router.push(props.to);
    suppressFlyout(props.label);
  }
};
</script>

<style scoped>
/* The same curves as the mobile menu (Tailwind's ease-out for what appears, ease-in for what leaves), so both feel
   alike: */
.mega-menu-sheet,
.mega-menu-content,
.mega-menu-backdrop {
  --mega-menu-ease-out: cubic-bezier(0, 0, 0.2, 1);
  --mega-menu-ease-in: cubic-bezier(0.4, 0, 1, 1);
}

/* The clip-path cuts off the part of the shadow that would show above the panel, over the header's edge. Opening
   animates its bottom inset from 100% (nothing shown) to this, so the sheet unrolls down from the menu. The height
   follows the content (see sheetHeight). */
.mega-menu-sheet {
  clip-path: inset(0 0 -20px 0);
  transition: height 250ms var(--mega-menu-ease-out);
}

/* Opening and closing. */
.mega-menu-enter-active .mega-menu-sheet {
  transition:
    clip-path 300ms var(--mega-menu-ease-out),
    height 250ms var(--mega-menu-ease-out);
}
.mega-menu-enter-active .mega-menu-content {
  transition:
    opacity 200ms var(--mega-menu-ease-out) 80ms,
    transform 200ms var(--mega-menu-ease-out) 80ms;
}
.mega-menu-enter-active .mega-menu-backdrop {
  transition: opacity 250ms var(--mega-menu-ease-out);
}
.mega-menu-leave-active .mega-menu-sheet {
  transition: clip-path 200ms var(--mega-menu-ease-in) 50ms;
}
.mega-menu-leave-active .mega-menu-content {
  transition: opacity 150ms var(--mega-menu-ease-in);
}
.mega-menu-leave-active .mega-menu-backdrop {
  transition: opacity 200ms var(--mega-menu-ease-in) 50ms;
}
.mega-menu-enter-from .mega-menu-sheet,
.mega-menu-leave-to .mega-menu-sheet {
  clip-path: inset(0 0 100% 0);
}
.mega-menu-enter-from .mega-menu-content {
  opacity: 0;
  transform: translateY(-12px);
}
.mega-menu-leave-to .mega-menu-content {
  opacity: 0;
}
.mega-menu-enter-from .mega-menu-backdrop,
.mega-menu-leave-to .mega-menu-backdrop {
  opacity: 0;
}

/* Swapping to another nav item's panel: the old one goes at once, the new sheet is there straight away (animating from
   the old height) and its content fades in. */
.mega-menu-swap-leave-active {
  visibility: hidden;
}
.mega-menu-swap-enter-active .mega-menu-content {
  transition:
    opacity 200ms var(--mega-menu-ease-out) 60ms,
    transform 200ms var(--mega-menu-ease-out) 60ms;
}
.mega-menu-swap-enter-from .mega-menu-content {
  opacity: 0;
  transform: translateY(-8px);
}

@media (prefers-reduced-motion: reduce) {
  .mega-menu-sheet,
  .mega-menu-enter-active *,
  .mega-menu-leave-active *,
  .mega-menu-swap-enter-active * {
    transition: none !important;
  }
}
</style>
